/**
 * SSOT Registry — data-driven catalog of every single-source-of-truth.
 *
 * GOVERNANCE_DETANGLE_PLAN_2026 §17 (SSOT consolidation + admin insight).
 *
 * The codebase has many SSOTs (tier table, DOK floors/ceilings, module
 * cognitive profiles, per-type cognitive bounds, question-type ceilings,
 * visual roles, …). They were discoverable only by reading source. This
 * registry turns "which SSOTs exist + what do they currently say" into
 * DATA: each entry carries metadata PLUS a `read()` that returns the LIVE
 * values from the actual SSOT at call time, so the catalog can never drift
 * from code and can be rendered for insight in the admin portal.
 */

/** Broad domain a SSOT belongs to — used for grouping in the admin. */
/**
 * `design` is er bewust NIET meer (12 aug 2026).
 *
 * De design-tokens stonden zowel hier als op de eigen Design System-pagina, en
 * dat waren twee UI's over dezelfde bron. Er zat geen waardeverschil in — beide
 * lazen `config/designTokens.ts` live — maar twee schermen die hetzelfde tonen,
 * gaan onvermijdelijk uiteenlopen in dekking. Dat was al gebeurd: dit register
 * toonde 4 van de 7 semantische tokens en kende `SHADOW` en `BADGE_VARIANTS`
 * helemaal niet.
 *
 * De visuele pagina heeft gewonnen om één reden: een kleur is niet te beoordelen
 * uit een hex-string in een tabel. Voor tokens is de swatch het punt.
 */
export type SsotDomain = 'taxonomy' | 'cognitive' | 'governance' | 'pipeline' | 'content' | 'visuals' | 'mastery' | 'platform';

/** A single column in a SSOT's live snapshot table. */
export interface SsotColumn {
  key: string;
  label: string;
}

/**
 * A live, render-ready view of a SSOT's current values. Computed on demand
 * by {@link SsotEntry.read} so it always reflects the running code.
 */
export interface SsotSnapshot {
  columns: SsotColumn[];
  rows: Array<Record<string, string | number>>;
}

/**
 * Uitkomst van een live bewaking op één entry.
 *
 * WAAROM DIT OP DE ENTRY ZIT EN NIET IN HET DIAGNOSTIEK-SCHERM
 *
 * De bewaking hoorde eerst in het adminportaal, met daar een eigen kopie van
 * "wat heet deze bron" en "welk bestand is het". Dat is precies de fout die dit
 * register moet uitbannen: dan bestaat er een tweede beschrijving van dezelfde
 * bron, en die loopt uiteen zodra iemand er één bijwerkt.
 *
 * Nu bezit de entry zowel de waarden (`read`) als de bewaking (`guard`), en
 * leent het diagnostiek-scherm de naam en het bestandspad van de entry.
 */
export interface SsotGuardResult {
  /** Staan de bronnen gelijk? */
  ok: boolean;
  /** Wat een beheerder eraan heeft: het gevolg, niet de mechaniek. */
  detail: string;
}

/** One catalogued single-source-of-truth. */
export interface SsotEntry {
  /** Stable identifier (kebab-case). */
  id: string;
  /** Human-readable name shown in the admin. */
  name: string;
  /** Domain grouping. */
  domain: SsotDomain;
  /** Source file path (for "go to source" affordance). */
  file: string;
  /** Canonical export(s) that constitute the SSOT. */
  exportName: string;
  /** One-line description of what this SSOT is. */
  description: string;
  /** What decision / behaviour this SSOT drives. */
  governs: string;
  /** Known read-sites / consumers. */
  consumers: string[];
  /** Optional drift / caveat note (e.g. shadow-gated reconciliation). */
  notes?: string;
  /** Returns the LIVE values of this SSOT, computed at call time. */
  read: () => SsotSnapshot;
  /**
   * Optionele live bewaking: legt deze bron naast een consument die hem hoort
   * te volgen. Slaat hij om, dan is er werkelijk een tweede definitie ontstaan.
   *
   * Alleen invullen als de vergelijking in de browser te máken is. Een bewaking
   * die niets kan meten hoort niet hier maar als openstaand punt in
   * Diagnostiek — anders toont het register groen op een aanname.
   */
  guard?: () => SsotGuardResult;
}